import { supabaseUserMiddleware } from "@/middleware/supabase-user-middleware";
import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { parseISO } from "date-fns";
import z from "zod";
import { buildOrderConfirmationLineData } from "./order-confirmation-email";
import { createClient } from "./supabase/admin";

// How often the status page re-asks the server on its own. The POS pushes
// every status change over realtime; the poll only covers a phone that was
// locked or backgrounded long enough to miss a push.
export const KIOSK_ORDER_POLL_INTERVAL_MS = 30_000;

export const kioskOrderQueryOptions = (orderId: string, secret?: string) =>
  queryOptions({
    queryKey: ["kiosk-order", orderId, secret],
    queryFn: () => getKioskOrder({ data: { orderId, secret } }),
    refetchInterval: KIOSK_ORDER_POLL_INTERVAL_MS,
  });

/**
 * The order behind the QR code on a kiosk ticket.
 *
 * Looked up by order id rather than reference: a kiosk reference is a daily
 * counter ("003") that repeats every day, so only the id is unique. The secret
 * printed alongside it is what makes the link private; both are minted by the
 * kiosk when it writes the order, and the merchant is the one this host
 * belongs to, so a guessed id from another shop answers nothing.
 */
export const getKioskOrder = createServerFn({ method: "GET" })
  .middleware([supabaseUserMiddleware])
  .inputValidator(
    z.object({ orderId: z.string().uuid(), secret: z.string().optional() }),
  )
  .handler(async ({ context: { userId }, data }) => {
    if (!data.secret) {
      throw new Error("Secret is required");
    }

    const supabase = createClient();

    const { data: order, error: orderError } = await supabase
      .from("online_order")
      .select(
        "number, isPaid:is_paid, confirmedTime:confirmed_time, orderStatus:order_status, orderType:order_type, order!inner(reference, secret, createdAt:created_at, items:order_line(name, quantity, price, type, order_discount_allocation_in_cents))",
      )
      .eq("order_id", data.orderId)
      .eq("order.secret", data.secret)
      .eq("platform", "kiosk")
      .eq("user_id", userId)
      .maybeSingle();

    if (orderError) {
      console.error("Could not get kiosk order", orderError);
      throw new Error("Could not get order");
    }

    if (!order) {
      throw new Error("Order not found");
    }

    const orderLines = order.order.items.filter((line) => line.quantity > 0);
    const { items, fees, discount } = buildOrderConfirmationLineData(
      orderLines,
      "Discount",
    );
    const itemsSubtotal = items.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0,
    );
    const feesTotal = (fees ?? []).reduce((sum, fee) => sum + fee.amount, 0);
    const total = itemsSubtotal + feesTotal - (discount?.amount ?? 0);

    return {
      // What the ticket and the take-away screen call the order by.
      reference: order.order.reference,
      // The realtime channel the POS publishes status changes on is keyed on
      // reference + secret; the page needs the reference to listen.
      secret: order.order.secret,
      status: order.orderStatus,
      orderType: order.orderType,
      isPaid: order.isPaid,
      createdAt: parseISO(order.order.createdAt),
      confirmedTime: order.confirmedTime ? parseISO(order.confirmedTime) : null,
      summary: {
        items,
        fees,
        discount,
        subtotal: itemsSubtotal,
        total,
      },
    };
  });

export type KioskOrder = Awaited<ReturnType<typeof getKioskOrder>>;
